import type { ItemId, OptionId, QuestionId, TargetId } from './brands.ts';

/**
 * What a candidate did, and what it was worth.
 *
 * Kept separate from the exam model because it has a different owner and a different lifetime: the
 * exam is content someone published, a response is something a person did with it.
 */

export type Response =
  | { readonly type: 'single-choice'; readonly optionId: OptionId }
  | { readonly type: 'multiple-choice'; readonly optionIds: readonly OptionId[] }
  | {
      readonly type: 'matching';
      readonly pairs: readonly { readonly itemId: ItemId; readonly targetId: TargetId }[];
    }
  | { readonly type: 'ordering'; readonly order: readonly ItemId[] }
  | { readonly type: 'text'; readonly text: string };

export type ResponseByType = { [R in Response as R['type']]: R };

/**
 * The five ways a question can end up.
 *
 * `ungraded` exists because free-text questions marked `evaluation: "self"` cannot be scored
 * automatically. Without it they would have to be counted as wrong, which would quietly punish
 * candidates for answering the open questions — so they are counted in the total and excluded from
 * the percentage until the candidate resolves them during review.
 */
export type Outcome = 'correct' | 'incorrect' | 'partial' | 'unanswered' | 'ungraded';

export interface QuestionScore {
  readonly raw: number;
  readonly max: number;
  readonly outcome: Outcome;
}

/**
 * `correct` is derived, never stored.
 *
 * Storing both a score and a boolean invites them to disagree — and once partial credit exists,
 * the boolean is the one that would be wrong.
 */
export function isCorrect(score: QuestionScore): boolean {
  return score.max > 0 && score.raw === score.max;
}

/** Whether this result contributes to the percentage at all. */
export function isGraded(score: QuestionScore): boolean {
  return score.outcome !== 'ungraded';
}

export type Confidence = 'low' | 'medium' | 'high';

export type ReportReason =
  | 'incorrect-answer'
  | 'ambiguous-question'
  | 'incorrect-explanation'
  | 'outdated-content'
  | 'conversion-error'
  | 'translation-error'
  | 'other';

export interface QuestionReport {
  readonly questionId: QuestionId;
  readonly reasons: readonly ReportReason[];
  readonly comment?: string;
  readonly createdAt: string;
}

/** Whether a response carries an actual answer, as opposed to an empty shell. */
export function isAnswered(response: Response | null): boolean {
  if (response === null) {
    return false;
  }
  switch (response.type) {
    case 'single-choice':
      return response.optionId.length > 0;
    case 'multiple-choice':
      return response.optionIds.length > 0;
    case 'matching':
      return response.pairs.length > 0;
    case 'ordering':
      return response.order.length > 0;
    case 'text':
      return response.text.trim().length > 0;
  }
}
